import React, { useEffect, useState } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import {
  ArrowLeft,
  ShoppingCart,
  ShoppingBag,
  Info,
  Star,
  Truck,
} from 'lucide-react';
import httpClient from '../api/axios';

const ProductDetail = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const [product, setProduct] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [quantity, setQuantity] = useState(1);
  const [snackbar, setSnackbar] = useState({
    open: false,
    message: '',
    severity: 'success',
  });

  useEffect(() => {
    const fetchProduct = async () => {
      try {
        const response = await httpClient.get(`/api/products/${id}`);
        if (response.data && response.data._id) {
          setProduct(response.data);
        } else {
          throw new Error('Invalid product data');
        }
      } catch (err) {
        console.error('Product fetch error:', err);
        setError(err.response?.data?.message || 'Product not found');
      } finally {
        setLoading(false);
      }
    };

    fetchProduct();
  }, [id]);

  const buyNow = (productToAdd) => {
    navigate(`/cart/${productToAdd?.id}/${quantity}`);
  };

  const handleQuantityChange = (e) => {
    const value = Math.max(1, Math.min(100, Number(e.target.value)));
    setQuantity(value);
  };

  const handleAddToCart = () => {
    // Implement your cart logic here
    setSnackbar({
      open: true,
      message: `${quantity}kg of ${product.name} added to cart`,
      severity: 'success',
    });
  };

  const handleBuyNow = () => {
    navigate('/cart', {
      state: {
        product: {
          ...product,
          quantityKg: quantity,
        },
      },
    });
  };

  const SetQuantityIncreaseFunc = () => {
    if (quantity < 10) {
      setQuantity(quantity + 1);
    }
  };
  const SetQuantityDecreaseFunc = () => {
    if (quantity > 1) {
      setQuantity(quantity - 1);
    }
  };

  if (loading) {
    return (
      <div className='max-w-6xl mx-auto px-4 py-8 mt-20 animate-pulse'>
        <div className='mb-6 h-6 w-32 bg-gray-200 rounded'></div>
        <div className='grid md:grid-cols-2 gap-8'>
          <div className='h-96 bg-gray-200 rounded-xl'></div>
          <div className='space-y-4'>
            <div className='h-8 bg-gray-200 w-3/4 rounded'></div>
            <div className='h-4 bg-gray-200 w-1/4 rounded'></div>
            <div className='h-4 bg-gray-200 w-full rounded'></div>
            <div className='h-4 bg-gray-200 w-2/3 rounded'></div>
            <div className='h-12 bg-gray-200 w-48 rounded'></div>
          </div>
        </div>
      </div>
    );
  }

  if (error || !product) {
    return (
      <div className='max-w-6xl mx-auto px-4 py-8 mt-20 text-center'>
        <div className='inline-block p-6 bg-red-50 rounded-lg'>
          <Info className='w-12 h-12 text-red-600 mx-auto mb-4' />
          <h2 className='text-2xl font-semibold text-red-600 mb-4'>
            Product Not Found
          </h2>
          <p className='text-gray-600 mb-6'>
            {error || "The requested product doesn't exist."}
          </p>
          <div className='flex justify-center gap-4'>
            <button
              onClick={() => navigate(-1)}
              className='bg-gray-100 text-gray-700 px-6 py-2 rounded-lg hover:bg-gray-200 flex items-center'
            >
              <ArrowLeft className='mr-2' /> Go Back
            </button>
            <Link
              to='/products'
              className='bg-emerald-600 text-white px-6 py-2 rounded-lg hover:bg-emerald-700'
            >
              Browse Products
            </Link>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className='max-w-6xl mx-auto px-4 py-8 mt-20'>
      <button
        onClick={() => navigate(-1)}
        className='mb-8 flex items-center text-emerald-600 hover:text-emerald-700'
      >
        <ArrowLeft className='mr-2' /> Back to Products
      </button>

      <div className='grid md:grid-cols-2 gap-8'>
        {/* Image Section */}
        <div className='bg-gray-50 rounded-xl p-6'>
          <img
            src={product.productImg || '/placeholder-product.jpg'}
            alt={product.name}
            className='w-full h-96 object-contain rounded-lg'
            onError={(e) => {
              e.target.src = '/placeholder-product.jpg';
            }}
          />
        </div>

        {/* Product Info */}
        <div className='space-y-6'>
          <div>
            <span className='inline-block bg-emerald-100 text-emerald-700 px-3 py-1 rounded-full text-sm'>
              {product.category}
            </span>
            <h1 className='mt-4 text-3xl font-bold text-gray-900'>
              {product.name}
            </h1>
          </div>

          <div className='text-2xl font-bold text-emerald-600'>
            ₹{product.pricePerKg?.toFixed(2)}
          </div>

          <div className='prose text-gray-600'>
            <p>{product.description || 'No description available'}</p>
          </div>

          <div className='border-t border-b border-gray-200 py-6'>
            <div className='flex items-center gap-4'>
              <Truck className='w-8 h-8 text-emerald-600' />
              <div>
                <h3 className='font-semibold'>Free Shipping</h3>
                <p className='text-sm text-gray-600'>
                  Delivered within 3-5 business days
                </p>
              </div>
            </div>
          </div>

          {/* Quantity Selector */}
          <div className='space-y-4'>
            <label className='block text-sm font-medium text-gray-700'>
              Quantity (kg)
            </label>
            <div className='flex items-center gap-2'>
              <button
                onClick={SetQuantityDecreaseFunc}
                className='px-4 py-2 border rounded-lg hover:bg-gray-50'
              >
                -
              </button>
              <input
                type='number'
                value={quantity}
                onChange={handleQuantityChange}
                className='w-20 text-center px-4 py-2 border rounded-lg'
                min='1'
                max='100'
              />
              <button
                onClick={SetQuantityIncreaseFunc}
                className='px-4 py-2 border rounded-lg hover:bg-gray-50'
              >
                +
              </button>
            </div>
          </div>

          {/* Total Price */}
          <div className='text-xl font-bold text-gray-900'>
            Total: ₹{(product.pricePerKg * quantity).toFixed(2)}
          </div>

          {/* Action Buttons */}
          <div className='grid sm:grid-cols-2 gap-4'>
            <button
              onClick={() =>
                buyNow({
                  id: product._id,
                  name: product.name,
                  productImg: product.productImg,
                  pricePerKg: product.pricePerKg,
                  quantityKg: quantity,
                })
              }
              className='flex items-center justify-center py-2 px-4 bg-gradient-to-r from-emerald-600 to-emerald-500 text-white rounded-md hover:from-emerald-700 hover:to-emerald-600 transition-all text-sm font-medium'
              disabled={product.quantity === 0}
            >
              <ShoppingBag className='mr-2' size={16} />
              Buy Now
            </button>
          </div>

          {/* Stock Status */}
          {product.quantity === 0 ? (
            <div className='text-red-600 font-medium'>
              Currently out of stock
            </div>
          ) : (
            <div className='text-gray-600'>
              {product.quantity} kg available in stock
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default ProductDetail;
